import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowUp } from 'lucide-react';

import { askHelpAssistantSchema } from '@/app/helpArticle/_validators';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/app/ui/form';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupText, InputGroupTextarea } from '@/app/ui/input-group';

/**
 * The question box, with the send button inside it. Enter sends, Shift+Enter
 * starts a new line, and the button waits for something to send. It clears as soon as a question goes, and the question
 * shows in the conversation instead.
 */
const Composer = ({ isThinking, onAsk }) => {
  const form = useForm({ resolver: zodResolver(askHelpAssistantSchema), defaultValues: { question: '' } });
  const isBlank = !form.watch('question').trim();

  const onSubmit = (values) => {
    form.reset({ question: '' });
    onAsk(values.question);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      if (!isThinking) {
        form.handleSubmit(onSubmit)();
      }
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="question"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">Question</FormLabel>
              <InputGroup className="bg-background rounded-xl">
                <FormControl>
                  <InputGroupTextarea
                    {...field}
                    placeholder="Ask a question…"
                    className="max-h-40 min-h-0 overflow-auto px-3.5 pt-3 pb-0"
                    onKeyDown={handleKeyDown}
                    autoFocus
                    data-testid="question-input"
                  />
                </FormControl>
                <InputGroupAddon align="block-end" className="justify-between pt-1 pr-2 pb-2 pl-3.5">
                  <InputGroupText className="text-xs">Shift + Enter for a new line</InputGroupText>
                  <InputGroupButton type="submit" variant="default" size="icon-sm" className="rounded-full" disabled={isThinking || isBlank} data-testid="modal-submit-button">
                    <ArrowUp />
                    <span className="sr-only">Send</span>
                  </InputGroupButton>
                </InputGroupAddon>
              </InputGroup>
              <FormMessage data-testid="question-input-error" />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};

export default Composer;
